import { useState } from 'react';

import { ActionMenu } from '@/components/common/action-menu';
import { FeedbackPrompt } from '@/features/feedback';
import { RaiseSupportTicketFlow } from '@/features/support';
import { WarrantyClaimFlow, useWarrantyEligibility } from '@/features/warranty';
import { formatIsoDate } from '@/lib/utils/format';

import type { Device } from '../types';

import { HistoryDrawer } from './history-drawer';
import { RenameDeviceModal } from './rename-device-modal';

export type DeviceActionsMenuProps = {
  device: Device;
};

/**
 * Kebab action menu for the detail header (Figma 2207-463879): Rename (modal), Show
 * History (drawer), Raise Support Ticket (story 101869), Claim Warranty (story 101871 —
 * gated by eligibility), and Provide Feedback (placeholder for this slice).
 */
export function DeviceActionsMenu({ device }: DeviceActionsMenuProps) {
  const [renameOpen, setRenameOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [warrantyOpen, setWarrantyOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  // Eligibility gate (AC §1): expired / open-claim disable the Claim Warranty action with a
  // reason. Falls back to the device's own warranty flag until the server check resolves.
  const eligibility = useWarrantyEligibility(device.id);
  const status = eligibility.data?.status ?? (device.warranty.active ? 'eligible' : 'expired');
  const expiresAt =
    eligibility.data?.expiresAt ?? device.warranty.endsAt ?? device.metadata.warrantyEnd;
  const warrantyBlocked = status !== 'eligible';
  const warrantyReason =
    status === 'expired'
      ? `Warranty expired on ${expiresAt ? formatIsoDate(expiresAt) : 'an earlier date'}`
      : status === 'open_claim'
        ? 'Claim already in progress'
        : undefined;

  const groups = [
    {
      items: [
        { key: 'rename', label: 'Rename Device', onClick: () => setRenameOpen(true) },
        { key: 'history', label: 'Show History', onClick: () => setHistoryOpen(true) },
        { key: 'support', label: 'Raise Support Ticket', onClick: () => setSupportOpen(true) },
        {
          key: 'warranty',
          label: 'Claim Warranty',
          description: warrantyReason,
          disabled: warrantyBlocked,
          onClick: () => setWarrantyOpen(true),
        },
        {
          key: 'feedback',
          label: 'Provide Feedback',
          onClick: () => setFeedbackOpen(true),
        },
      ],
    },
  ];

  return (
    <>
      <ActionMenu groups={groups} triggerLabel="Device actions" />
      <RenameDeviceModal open={renameOpen} device={device} onClose={() => setRenameOpen(false)} />
      <HistoryDrawer
        open={historyOpen}
        deviceId={device.id}
        onClose={() => setHistoryOpen(false)}
      />
      <RaiseSupportTicketFlow
        open={supportOpen}
        device={device}
        onClose={() => setSupportOpen(false)}
      />
      <WarrantyClaimFlow
        open={warrantyOpen}
        devices={[device]}
        lockedDeviceId={device.id}
        onClose={() => setWarrantyOpen(false)}
      />
      <FeedbackPrompt open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </>
  );
}
